import { useCallback, useEffect, useMemo, useState } from "react";
import { CurrencyCode, DEFAULT_RATES_FROM_AUD, formatFromAud, formatMoney } from "@/lib/currency";

const STORAGE_KEY = "ss_currency";
const RATES_KEY = "ss_currency_rates";
const RATES_UPDATED_KEY = "ss_currency_rates_updated_at";
const RATE_TTL_MS = 24 * 60 * 60 * 1000;

const readStoredCurrency = () => {
  if (typeof window === "undefined") return null;
  return (window.localStorage.getItem(STORAGE_KEY) as CurrencyCode | null) || null;
};

export const useCurrency = () => {
  const [currency, setCurrencyState] = useState<CurrencyCode>(() => {
    return readStoredCurrency() || "AUD";
  });
  const [rates, setRates] = useState<Record<CurrencyCode, number>>(DEFAULT_RATES_FROM_AUD);
  const [ratesUpdatedAt, setRatesUpdatedAt] = useState<number | null>(null);
  const [usingLiveRates, setUsingLiveRates] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, currency);
    window.dispatchEvent(new Event("currency-change"));
  }, [currency]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const storedRates = window.localStorage.getItem(RATES_KEY);
    const storedUpdated = window.localStorage.getItem(RATES_UPDATED_KEY);
    if (storedRates) {
      try {
        const parsed = JSON.parse(storedRates) as Record<CurrencyCode, number>;
        setRates({ ...DEFAULT_RATES_FROM_AUD, ...parsed });
        setUsingLiveRates(true);
      } catch {
        setRates(DEFAULT_RATES_FROM_AUD);
      }
    }
    if (storedUpdated) {
      const ts = Number(storedUpdated);
      if (Number.isFinite(ts)) setRatesUpdatedAt(ts);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const now = Date.now();
    const last = ratesUpdatedAt ?? 0;
    if (now - last < RATE_TTL_MS) return;

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 6000);

    const lowerCodes = Object.keys(DEFAULT_RATES_FROM_AUD).map((code) => code.toLowerCase());
    const primaryUrl = "https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/aud.min.json";
    const fallbackUrl = "https://latest.currency-api.pages.dev/v1/currencies/aud.min.json";

    const fetchRates = async () => {
      const res = await fetch(primaryUrl, { signal: controller.signal });
      if (res.ok) return res.json();
      const fallback = await fetch(fallbackUrl, { signal: controller.signal });
      if (!fallback.ok) throw new Error("FX fetch failed");
      return fallback.json();
    };

    fetchRates()
      .then((data) => {
        const payload = data?.aud;
        if (!payload) return;
        const nextRates = { ...DEFAULT_RATES_FROM_AUD };
        lowerCodes.forEach((codeLower) => {
          const upper = codeLower.toUpperCase() as CurrencyCode;
          const value = Number(payload[codeLower]);
          if (Number.isFinite(value) && value > 0 && upper in nextRates) {
            nextRates[upper] = value;
          }
        });
        setRates(nextRates);
        setUsingLiveRates(true);
        const updatedAt = Date.now();
        setRatesUpdatedAt(updatedAt);
        window.localStorage.setItem(RATES_KEY, JSON.stringify(nextRates));
        window.localStorage.setItem(RATES_UPDATED_KEY, String(updatedAt));
      })
      .catch(() => {
        setRates(DEFAULT_RATES_FROM_AUD);
        setUsingLiveRates(false);
      })
      .finally(() => window.clearTimeout(timeout));
  }, [ratesUpdatedAt]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => {
      const stored = readStoredCurrency();
      if (stored && stored !== currency) {
        setCurrencyState(stored);
      }
    };
    window.addEventListener("storage", handler);
    window.addEventListener("currency-change", handler);
    return () => {
      window.removeEventListener("storage", handler);
      window.removeEventListener("currency-change", handler);
    };
  }, [currency]);

  const setCurrency = useCallback((next: CurrencyCode) => {
    setCurrencyState(next);
  }, []);

  const formatAud = useCallback((amount: number) => formatMoney(amount, "AUD"), []);
  const formatCurrency = useCallback(
    (amountAud: number) => formatFromAud(amountAud, currency, rates),
    [currency, rates],
  );

  return useMemo(
    () => ({
      currency,
      setCurrency,
      formatCurrency,
      formatAud,
      ratesUpdatedAt,
      usingLiveRates,
    }),
    [currency, formatAud, formatCurrency, ratesUpdatedAt, setCurrency, usingLiveRates],
  );
};
